import { Button, Stack } from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CourseApi } from '../../api/CourseApi';
import { CourseOfferingApi } from '../../api/CourseOfferingApi';
import { DialogApi } from '../../api/DialogApi';
import { DialogPromptStartClass } from '../../api/DialogPromptApi';
import { LessonApi } from '../../api/LessonApi';
import AppBarContext from '../../context/AppBarContext';
import DialogPromptFooterContext from '../../context/DialogPromptFooterContext';
import { isSettingsCourse } from '../../page/Lesson';
import ChatBubble from '../ChatBubble';
import { handleErrorMessage } from '../ErrorDialog';
import { getAudioByText } from './DialogPromptComponent';

interface DialogPromptStartClassComponentProps {
    dialogPrompt: DialogPromptStartClass;
    setDialogPromptComplete: (dialogPromptId: number) => void;
    showAvatar?: boolean;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>;
    isSettings?: boolean;
}

const DialogPromptStartClassComponent: React.FC<DialogPromptStartClassComponentProps> =
    ({ dialogPrompt, setDialogPromptComplete, showAvatar = true, chatBubbleContainerRef }) => {

        const { setErrorMessage } = React.useContext(AppBarContext)
        const history = useHistory();
        const location = useLocation();

        const { setActionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

        const [response, setResponse] = React.useState<string | null>(null);

        const [chatBubbleDone, setChatBubbleDone] = React.useState(false);
        const [chatBubbleResponseDone, setChatBubbleResponseDone] = React.useState(false);
        const [nextDialogUrl, setNextDialogUrl] = React.useState<string | null>(null)


        const [promptAudio, setPromptAudio] = React.useState<Blob | null | undefined>(undefined);

        // const handleYesButtonClick = async () => {
        //     setResponse(dialogPrompt.yes_response);

        //     try {
        //         let courseId = -1;
        //         const courseOffering = await CourseOfferingApi.getCurrent();
        //         if (courseOffering.data !== null && courseOffering.data.course_id !== null) {
        //             courseId = courseOffering.data.course_id
        //         } else {
        //             const courseListResponse = await CourseApi.getAll();
        //             const courseList = courseListResponse.data.filter(course => course.is_hidden === false && isSettingsCourse(course) == false);
        //             courseId = courseList[0].id;
        //         }


        //         const lessonListResponse = await LessonApi.getAll(courseId);
        //         const lessonList = lessonListResponse.data.filter(lesson => lesson.is_hidden === false);

        //         const dialogListResponse = await DialogApi.getAll(lessonList[0].id);
        //         const dialogList = dialogListResponse.data.filter(dialog => dialog.is_hidden === false);

        //         setNextDialogUrl(`/lesson?dialogId=${dialogList[0].id}`)
        //     } catch (err) {
        //         console.error("Error getting lesson" + err)
        //         handleErrorMessage(err, setErrorMessage, history, location);
        //     }
        // }

        const handleYesButtonClick = React.useCallback(async () => {
            setResponse(dialogPrompt.yes_response);

            try {
                let courseId = -1;
                const courseOffering = await CourseOfferingApi.getCurrent();

                if (courseOffering.data && courseOffering.data.course_id != null) {
                    courseId = courseOffering.data.course_id;
                } else {
                    const courseListResponse = await CourseApi.getAll();
                    const courseList = courseListResponse.data.filter(
                        course => !course.is_hidden && !isSettingsCourse(course)
                    );

                    if (courseList.length === 0) throw new Error("No visible non-settings course found.");
                    courseId = courseList[0].id;
                }

                const lessonListResponse = await LessonApi.getAll(courseId);
                const lessonList = lessonListResponse.data.filter(lesson => !lesson.is_hidden);

                if (lessonList.length === 0) throw new Error("No visible lesson found.");
                const lesson = lessonList[0];

                const dialogListResponse = await DialogApi.getAll(lesson.id);
                const dialogList = dialogListResponse.data.filter(dialog => !dialog.is_hidden);

                if (dialogList.length === 0) throw new Error("No visible dialog found.");
                const dialog = dialogList[0];

                setNextDialogUrl(`/lesson/${courseId}/${lesson.id}/${dialog.id}`);
            } catch (err) {
                console.error("Error getting lesson: ", err);
                handleErrorMessage(err, setErrorMessage, history, location);
            }
        }, [dialogPrompt.yes_response, setErrorMessage, history, location]);


        const handleNoButtonClick = React.useCallback(() => {
            setResponse(dialogPrompt.no_response);
        }, [dialogPrompt.no_response]);

        React.useEffect(() => {
            clearFooterList()
            getAudioByText(history, setErrorMessage, dialogPrompt.prompt, setPromptAudio)
        }, [clearFooterList, dialogPrompt.prompt, history, setErrorMessage]);

        React.useEffect(() => {
            if (chatBubbleDone) {
                setActionList([
                    <Button variant="contained" color="primary" onClick={() => handleYesButtonClick()}>
                        {dialogPrompt.yes_button_text}
                    </Button>,
                    <Button variant="contained" color="secondary" onClick={() => handleNoButtonClick()} sx={{ ml: 2 }}>
                        {dialogPrompt.no_button_text}
                    </Button>
                ])
            }
        }, [chatBubbleDone, dialogPrompt.yes_button_text, dialogPrompt.no_button_text, handleYesButtonClick, handleNoButtonClick, setActionList]);

        React.useEffect(() => {
            if (chatBubbleResponseDone) {
                setDialogPromptComplete(dialogPrompt.id);
            }
            if (chatBubbleResponseDone && nextDialogUrl) {
                history.push(nextDialogUrl)
            }
        }, [chatBubbleResponseDone, nextDialogUrl, dialogPrompt.id, history, setDialogPromptComplete])

        return (
            <Stack>
                <ChatBubble
                    isTeacher={dialogPrompt.is_teacher}
                    message={dialogPrompt.prompt}
                    messageAudio={promptAudio ? promptAudio : null}
                    showAvatar={showAvatar}
                    setChatBubbleDone={setChatBubbleDone}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                />
                {response == null ? null : (
                    <ChatBubble
                        isTeacher={!dialogPrompt.is_teacher}
                        message={response}
                        messageAudio={null} // no audio for student
                        showAvatar={showAvatar}
                        setChatBubbleDone={setChatBubbleResponseDone}
                        chatBubbleContainerRef={chatBubbleContainerRef}
                    />
                )}
            </Stack>
        );
    };

export default DialogPromptStartClassComponent;
